import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { FilterSelect, SimpleSelect, FormField } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui';
import { LocationAddressFilterType } from '@/entities/location';

import { type ConsignmentSummary } from '@/entities/consignments';

import { useLocationSelect, getServiceTypeOptions, type ConsignmentFormValues } from '../model';

interface ServiceIdentificationCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
  consignmentData?: ConsignmentSummary;
  isReadOnly?: boolean;
}

export function ServiceIdentificationCard({
  form,
  consignmentData,
  isReadOnly = false
}: ServiceIdentificationCardProps) {
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const serviceType = watch('serviceType');
  const serviceDestination = watch('serviceDestination');

  const initialDestinationOption: SelectOption | undefined = useMemo(() => {
    if (!consignmentData?.destinationLocation?.id || !consignmentData?.destinationLocation?.description)
      return undefined;
    return {
      value: consignmentData.destinationLocation.id,
      label: consignmentData.destinationLocation.description
    };
  }, [consignmentData?.destinationLocation]);

  const { locationOptions, isLoading, onSearch, resetSearch } = useLocationSelect({
    filterType: serviceType,
    selectedValue: serviceDestination,
    initialOption: initialDestinationOption
  });

  const serviceTypeOptions = useMemo(() => getServiceTypeOptions(t), [t]);

  const handleServiceTypeChange = useCallback(
    (value: ConsignmentFormValues['serviceType']) => {
      setValue('serviceType', value, { shouldValidate: true });
      setValue('serviceDestination', '');
      resetSearch();
    },
    [resetSearch, setValue]
  );

  const showDurationField = serviceType === LocationAddressFilterType.STORAGE;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('request.serviceIdent.label')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FormField name="serviceType" error={errors.serviceType?.message} required>
            <Controller
              name="serviceType"
              control={control}
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['serviceType']>
                  value={field.value}
                  onChange={handleServiceTypeChange}
                  options={serviceTypeOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          <FormField
            label={t('request.destination.label')}
            name="serviceDestination"
            error={errors.serviceDestination?.message}>
            <Controller
              name="serviceDestination"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  options={locationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onInputValueChange={onSearch}
                  placeholder={isLoading ? t('common.loading') : t('gen.autocomplete.title')}
                  emptyMessage={t('dashboard.locations.empty')}
                  className="w-full"
                  disabled={isReadOnly}
                />
              )}
            />
          </FormField>

          {showDurationField && (
            <FormField
              label={t('request.storageDuration.label')}
              name="durationInDays"
              error={errors.durationInDays?.message}>
              <Input
                id="durationInDays"
                type="number"
                {...register('durationInDays')}
                placeholder="0"
                min="0"
                disabled={isReadOnly}
              />
            </FormField>
          )}

          <FormField
            label={t('request.arrivalInstructions.label')}
            name="serviceInstructions"
            error={errors.serviceInstructions?.message}>
            <Textarea
              id="serviceInstructions"
              {...register('serviceInstructions')}
              placeholder={t('request.arrivalInstructions.label')}
              rows={3}
              disabled={isReadOnly}
            />
          </FormField>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
